import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Lock, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (password.length < 6) { toast.error("Minimum 6 caractères"); return; }
    if (!token) { toast.error("Lien invalide - aucun token"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Mot de passe reinitialise!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lien expiré ou invalide");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md border border-zinc-200 rounded-2xl shadow-sm animate-fade-in" data-testid="reset-password-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-emerald-700 flex items-center justify-center">
            {done ? <CheckCircle className="h-6 w-6 text-white" /> : <Lock className="h-6 w-6 text-white" />}
          </div>
          <CardTitle className="font-heading text-2xl font-bold">
            {done ? "Mot de passe modifié!" : "Nouveau mot de passe"}
          </CardTitle>
          <CardDescription>
            {done ? "Vous pouvez maintenant vous connecter." : "Choisissez votre nouveau mot de passe."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <Button onClick={() => navigate("/login")} className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" data-testid="goto-login-btn">
                Se connecter
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 caractères" required className="mt-1" data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm">Confirmer</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmez le mot de passe" required className="mt-1" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" className="w-full bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl" disabled={loading} data-testid="reset-submit-btn">
                {loading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="justify-center">
          <Link to="/login" className="text-sm text-emerald-600 hover:underline font-medium" data-testid="back-to-login">
            Retour a la connexion
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
